/**
 * Security utilities for the integration monitoring application
 * Implements security measures for user validation and email security
 */

import { GetUserName } from '../services/AuthUser'
import createDOMPurify from 'dompurify'

/**
 * Basic email format validation (simplified - backend handles authorization)
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if email format is valid
 */
export const isValidEmailFormat = (email) => {
  if (!email || typeof email !== 'string') {
    return false
  }

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return emailRegex.test(email)
}

/**
 * Sanitizes user input to prevent HTML/script injection attacks
 * @param {string} input - The user input to sanitize
 * @returns {string} - Sanitized input safe for use
 */
const DOMPurify = createDOMPurify(window)
export const sanitizeUserInput = (input) => {
  if (!input || typeof input !== 'string') {
    return input
  }
  // Use DOMPurify to sanitize input
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).trim()
}
/**
 * Validates and sanitizes text input fields to prevent injection attacks
 * @param {string} input - The input to validate and sanitize
 * @param {number} maxLength - Maximum allowed length (default: 255)
 * @returns {object} - Validation result with sanitized value
 */
export const validateAndSanitizeTextInput = (input, maxLength = 255) => {
  const result = {
    isValid: true,
    sanitizedValue: '',
    errors: [],
  }

  if (!input) {
    result.sanitizedValue = ''
    return result
  }

  if (typeof input !== 'string') {
    result.isValid = false
    result.errors.push('Input must be a string')
    return result
  }

  // Check for suspicious patterns that might indicate HTML injection attempts
  const suspiciousPatterns = [
    /<script/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /<link/i,
    /<meta/i,
    /javascript:/i,
    /vbscript:/i,
    /data:/i,
    /onload=/i,
    /onclick=/i,
    /onerror=/i,
    /onmouseover=/i,
    /<a\s+href/i,
    /data:text\/html/i,
  ]

  const containsSuspiciousContent = suspiciousPatterns.some((pattern) => pattern.test(input))

  if (containsSuspiciousContent) {
    result.isValid = false
    result.errors.push('Input contains potentially malicious content')
  }

  // Sanitize the input
  result.sanitizedValue = sanitizeUserInput(input)

  // Check length after sanitization
  if (result.sanitizedValue.length > maxLength) {
    result.isValid = false
    result.errors.push(`Input exceeds maximum length of ${maxLength} characters`)
    result.sanitizedValue = result.sanitizedValue.substring(0, maxLength)
  }

  // Additional validation: ensure the sanitized content isn't empty if original wasn't
  if (input.trim().length > 0 && result.sanitizedValue.trim().length === 0) {
    result.isValid = false
    result.errors.push('Input contains only invalid characters')
  }

  return result
}

/**
 * Validates system name input specifically
 * @param {string} systemName - System name to validate
 * @returns {object} - Validation result
 */
export const validateSystemName = (systemName) => {
  const validation = validateAndSanitizeTextInput(systemName, 100)

  // Additional system name specific validation
  if (validation.sanitizedValue) {
    // Allow only alphanumeric, spaces, hyphens, and underscores
    const systemNamePattern = /^[a-zA-Z0-9\s\-_]+$/
    if (!systemNamePattern.test(validation.sanitizedValue)) {
      validation.isValid = false
      validation.errors.push(
        'System name can only contain letters, numbers, spaces, hyphens, and underscores',
      )
    }
  }

  return validation
}

/**
 * Validates integration name input specifically
 * @param {string} integrationName - Integration name to validate
 * @returns {object} - Validation result
 */
export const validateIntegrationName = (integrationName) => {
  const validation = validateAndSanitizeTextInput(integrationName, 100)

  // Additional integration name specific validation
  if (validation.sanitizedValue) {
    // Allow only alphanumeric, spaces, hyphens, and underscores
    const integrationNamePattern = /^[a-zA-Z0-9\s\-_]+$/
    if (!integrationNamePattern.test(validation.sanitizedValue)) {
      validation.isValid = false
      validation.errors.push(
        'Integration name can only contain letters, numbers, spaces, hyphens, and underscores',
      )
    }
  }

  return validation
}

/**
 * Validates flow name input specifically
 * @param {string} flowName - Flow name to validate
 * @returns {object} - Validation result
 */
export const validateFlowName = (flowName) => {
  const validation = validateAndSanitizeTextInput(flowName, 100)

  // Additional flow name specific validation
  if (validation.sanitizedValue) {
    // Allow only alphanumeric, spaces, hyphens, and underscores
    const flowNamePattern = /^[a-zA-Z0-9\s\-_]+$/
    if (!flowNamePattern.test(validation.sanitizedValue)) {
      validation.isValid = false
      validation.errors.push(
        'Flow name can only contain letters, numbers, spaces, hyphens, and underscores',
      )
    }
  }

  return validation
}

/**
 * Gets the current authenticated user's email (simplified - backend validates authorization)
 * @returns {string|null} - Authenticated user's email or null if not found
 */
export const getSecureAuthenticatedUserEmail = () => {
  const currentUser = GetUserName()?.[0]
  const userEmail = currentUser?.userEmail

  // First try to get email from the current user context
  if (userEmail && isValidEmailFormat(userEmail)) {
    return userEmail
  }

  // Fallback: Try to get from session storage
  const sessionEmail = sessionStorage.getItem('userSessionEmail')
  if (sessionEmail && sessionEmail !== 'null' && isValidEmailFormat(sessionEmail)) {
    return sessionEmail
  }

  // Fallback: Try to get from local storage
  const localEmail = localStorage.getItem('userSessionEmail')
  if (localEmail && localEmail !== 'null' && isValidEmailFormat(localEmail)) {
    return localEmail
  }

  // If all methods fail, log warning in development
  if (process.env.NODE_ENV === 'development') {
    console.warn('Unable to retrieve authenticated user email:', {
      currentUser,
      sessionEmail,
      localEmail,
    })
  }

  return null
}

/**
 * Validates current authentication state (simplified - backend handles authorization)
 * @returns {object} - Authentication state details
 */
export const getAuthenticationState = () => {
  const currentUser = GetUserName()?.[0]
  const userEmail = getSecureAuthenticatedUserEmail()
  const sessionEmail = sessionStorage.getItem('userSessionEmail')
  const localEmail = localStorage.getItem('userSessionEmail')
  const accessToken = sessionStorage.getItem('accessToken')

  const authState = {
    isAuthenticated: !!(userEmail && accessToken),
    userEmail: userEmail,
    userName: currentUser?.userName,
    hasValidToken: !!accessToken,
    tokenPresent: !!accessToken,
    emailSources: {
      currentUser: currentUser?.userEmail,
      session: sessionEmail,
      local: localEmail,
      resolved: userEmail,
    },
    validations: {
      emailFormat: userEmail ? isValidEmailFormat(userEmail) : false,
    },
  }

  // Log detailed state in development
  if (process.env.NODE_ENV === 'development') {
    console.log('Authentication State:', authState)
  }

  return authState
}

/**
 * Ensures that API request data contains the correct userId matching the authenticated user
 * @param {object} requestData - The request data object
 * @returns {object} - Modified request data with correct userId
 */
export const ensureSecureUserId = (requestData) => {
  const authenticatedUserEmail = getSecureAuthenticatedUserEmail()

  if (!requestData || !authenticatedUserEmail) {
    return requestData
  }

  // Ensure userId matches authenticated user
  if (requestData.userId) {
    requestData.userId = authenticatedUserEmail
  }

  return requestData
}

/**
 * Validates that the userId parameter matches the authenticated user (simplified)
 * @param {string} userId - The userId parameter from the request
 * @returns {boolean} - True if userId matches authenticated user
 */
export const validateUserIdMatchesAuthenticatedUser = (userId) => {
  const authenticatedUserEmail = getSecureAuthenticatedUserEmail()

  if (!authenticatedUserEmail || !userId) {
    return false
  }

  // Simple case-insensitive comparison - backend will handle authorization
  return authenticatedUserEmail.toLowerCase() === userId.toLowerCase()
}

/**
 * Sanitizes all text fields in an object to prevent injection attacks
 * @param {object} data - Object containing form data
 * @returns {object} - Object with sanitized text fields
 */
export const sanitizeFormData = (data) => {
  if (!data || typeof data !== 'object') {
    return data
  }

  const sanitizedData = { ...data }

  // Common text fields that need sanitization
  const textFields = [
    'systemName',
    'integrationName',
    'flowName',
    'name',
    'logicApp',
    'workSpaceId',
    'workspaceId',
    'description',
    'comments',
  ]

  textFields.forEach((field) => {
    if (sanitizedData[field] && typeof sanitizedData[field] === 'string') {
      const validation = validateAndSanitizeTextInput(sanitizedData[field])
      sanitizedData[field] = validation.sanitizedValue

      if (!validation.isValid) {
        console.warn(`Validation failed for field ${field}:`, validation.errors)
      }
    }
  })

  return sanitizedData
}

/**
 * Enhanced clickjacking protection utility
 * This provides additional runtime protection beyond CSP and frame-busting
 */
export const clickjackingProtection = {
  /**
   * Initialize clickjacking protection
   */
  init: () => {
    // Additional frame-busting protection
    if (window.top !== window.self) {
      // If we're in a frame, attempt to break out
      try {
        window.top.location = window.self.location
      } catch (e) {
        // If that fails, try alternative methods
        document.body.style.display = 'none'
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      }
    }

    // Monitor for frame changes - only in development
    if (process.env.NODE_ENV === 'development') {
      setInterval(() => {
        if (window.top !== window.self) {
          console.warn('Potential clickjacking detected')
          try {
            window.top.location = window.self.location
          } catch (e) {
            // Silent fail if cross-origin restrictions prevent access
          }
        }
      }, 5000) // Reduced frequency to 5 seconds
    }
  },

  /**
   * Check if application is running in a frame
   * @returns {boolean} - True if in frame
   */
  isInFrame: () => {
    return window.top !== window.self
  },

  /**
   * Validate referrer to prevent unauthorized embedding
   * @param {string[]} allowedReferrers - Array of allowed referrer domains
   * @returns {boolean} - True if referrer is valid
   */
  validateReferrer: (allowedReferrers = []) => {
    if (!document.referrer) return true // Direct access is allowed

    try {
      const referrerURL = new URL(document.referrer)
      const currentURL = new URL(window.location.href)

      // Same origin is always allowed
      if (referrerURL.origin === currentURL.origin) return true

      // Check against allowed referrers
      return allowedReferrers.some((allowed) => referrerURL.hostname.endsWith(allowed))
    } catch (e) {
      console.warn('Invalid referrer URL:', document.referrer)
      return false
    }
  },
}
